import { Link } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { button, buttonSize } from "~/components/Button.css";
import { card, icon, page } from "~/components/ErrorPage.css";
import { redirectToLoginOnExpiredSession } from "~/lib/sessionExpired";
import { Card, Stack } from "~/components";

// The router's defaultErrorComponent (TMF-169): what a rep sees when a route
// throws something the app didn't otherwise handle, instead of TanStack
// Router's built-in raw screen. Two cases:
//
//   - error IS an unauthorized HalError: this is a 401 the _app route guard
//     didn't catch because it surfaced while a route was already loading,
//     the same case queryClient's onError handles for a query or mutation
//     after mount (see queryClient.ts; both call the shared
//     redirectToLoginOnExpiredSession so only one of them ever actually
//     navigates). Tokens are already cleared, so this redirects the same way
//     rather than showing UI the rep would never read before the page
//     reloads anyway.
//   - anything else: a friendly page with a way back to the app, so an
//     unexpected error is never a dead end.
export function ErrorPage({ error }: ErrorComponentProps) {
  if (redirectToLoginOnExpiredSession(error)) return null;

  return (
    <main className={page}>
      <div className={card}>
        <Card>
          <Stack gap="md" align="center">
            <AlertTriangle size={32} className={icon} aria-hidden="true" />
            <h2>Something went wrong</h2>
            <p>
              This page hit an unexpected error. Try again, and if it keeps
              happening let an admin know.
            </p>
            <Link to="/" className={`${button.primary} ${buttonSize.md}`}>
              Go home
            </Link>
          </Stack>
        </Card>
      </div>
    </main>
  );
}
